/**
 * useGranolaIntegration — Granola AI direct API integration
 *
 * Users provide their Granola API key (grn_*). The system polls every 15 min
 * for new meeting notes, classifies them to projects, and creates documents.
 * Provides connection config, sync status, unassigned notes, and actions.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { bulkAssignNotes } from '@/lib/bulkNoteActions';

// ============================================================================
// TYPES
// ============================================================================

export interface GranolaConnectionStatus {
  configured: boolean;
  enabled?: boolean;
  totalIngested?: number;
  lastSyncedAt?: Date | null;
  lastError?: string | null;
  recentCount?: number;
  unassignedCount?: number;
}

export interface UnassignedGranolaNote {
  id: string;
  name: string;
  meetingDate: Date;
  attendees: string[];
  classificationConfidence: number;
  classificationReasoning: string;
  metadata: {
    granolaSummaryMarkdown?: string;
    granolaCalendarEvent?: {
      title: string;
      invitees: string[];
      organiser: string;
      startTime: string;
      endTime: string;
    } | null;
    granolaOwner?: { name: string; email: string };
  };
  createdAt: Date;
}

export interface SyncResult {
  success: boolean;
  notesFound: number;
  notesIngested: number;
  notesSkipped: number;
  errors: string[];
  durationMs: number;
}

// ============================================================================
// HOOK
// ============================================================================

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (typeof value === 'object' && value !== null && '_seconds' in value) {
    return new Date((value as { _seconds: number })._seconds * 1000);
  }
  return new Date(value as string | number);
}

export function useGranolaIntegration() {
  const { user, userProfile } = useAuth();
  const [status, setStatus] = useState<GranolaConnectionStatus>({ configured: false });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [unassignedNotes, setUnassignedNotes] = useState<UnassignedGranolaNote[]>([]);

  // Fetch connection status on mount
  useEffect(() => {
    if (!user?.uid) {
      setStatus({ configured: false });
      setLoadingStatus(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        const fn = httpsCallable(functions, 'getGranolaConnectionStatus');
        const result = await fn({});
        const data = result.data as GranolaConnectionStatus & { lastSyncedAt?: unknown };
        setStatus({
          ...data,
          lastSyncedAt: toDate(data.lastSyncedAt),
        });
      } catch (error) {
        console.error('[useGranolaIntegration] Failed to fetch status:', error);
      } finally {
        setLoadingStatus(false);
      }
    };

    fetchStatus();
  }, [user?.uid]);

  // Real-time listener for unassigned Granola notes
  const orgId = userProfile?.organizationId;
  useEffect(() => {
    if (!user?.uid || !orgId) {
      setUnassignedNotes([]);
      return;
    }

    const q = query(
      collection(db, 'documents'),
      where('transcriptSource', '==', 'granola'),
      where('organizationId', '==', orgId),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const notes: UnassignedGranolaNote[] = snap.docs
          .filter((d) => d.data().projectId == null)
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: data.name || 'Untitled Meeting',
              meetingDate: data.meetingDate?.toDate?.() || new Date(),
              attendees: data.attendees || [],
              classificationConfidence: data.classificationConfidence || 0,
              classificationReasoning: data.classificationReasoning || '',
              metadata: {
                granolaSummaryMarkdown: data.metadata?.granolaSummaryMarkdown,
                granolaCalendarEvent: data.metadata?.granolaCalendarEvent,
                granolaOwner: data.metadata?.granolaOwner,
              },
              createdAt: data.createdAt instanceof Date ? data.createdAt : (data.createdAt?.toDate?.() || new Date()),
            };
          })
          .sort((a, b) => b.meetingDate.getTime() - a.meetingDate.getTime());

        setUnassignedNotes(notes);
      },
      (error) => {
        console.error('[useGranolaIntegration] Unassigned listener error:', error);
      }
    );

    return () => unsub();
  }, [user?.uid, orgId]);

  const connect = useCallback(async (apiKey: string) => {
    setSaving(true);
    try {
      const fn = httpsCallable(functions, 'configureGranolaConnection');
      await fn({ apiKey });

      setStatus((prev) => ({
        ...prev,
        configured: true,
        enabled: true,
        lastError: null,
      }));

      toast.success('Granola connected! Sync will start within 15 minutes.');
      return true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to connect Granola';
      toast.error(msg);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    setSaving(true);
    try {
      const fn = httpsCallable(functions, 'disconnectGranola');
      await fn({});

      setStatus({ configured: false });
      toast.success('Granola disconnected');
      return true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to disconnect';
      toast.error(msg);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const syncNow = useCallback(async (): Promise<SyncResult | null> => {
    setSyncing(true);
    try {
      const fn = httpsCallable<Record<string, never>, SyncResult>(functions, 'syncGranolaNow', {
        timeout: 300_000,
      });
      const result = await fn({});
      const data = result.data;

      if (data.notesIngested > 0) {
        toast.success(`Synced ${data.notesIngested} new meeting note${data.notesIngested > 1 ? 's' : ''}`);
      } else if (data.notesFound === 0) {
        toast.info('No new meeting notes found');
      } else {
        toast.info(`${data.notesSkipped} note(s) already synced`);
      }

      // Refresh status
      setStatus((prev) => ({
        ...prev,
        lastSyncedAt: new Date(),
        totalIngested: (prev.totalIngested || 0) + data.notesIngested,
      }));

      return data;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Sync failed';
      toast.error(msg);
      return null;
    } finally {
      setSyncing(false);
    }
  }, []);

  const assignToProject = useCallback(async (documentId: string, projectId: string) => {
    try {
      await bulkAssignNotes([documentId], projectId);
    } catch (error) {
      console.error('[useGranolaIntegration] Assign failed:', error);
      toast.error('Failed to assign meeting note');
    }
  }, []);

  return {
    status,
    loadingStatus,
    saving,
    syncing,
    unassignedNotes,
    unassignedCount: unassignedNotes.length,
    connect,
    disconnect,
    syncNow,
    assignToProject,
  };
}
